import {Injectable, OnDestroy, OnInit} from '@angular/core';
import {ParentAPIService} from '../parent.api.service';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {ToastService} from '../toast-service';
import {Trade} from "../../model/clearing/trade";
import {CrystalclearClearingBackUrl} from "../config/crystalclear-clearing-back-url";
import {AllocationRequest} from "../../model/clearing/allocation/allocation-request";
import {ApiValidation} from "../../model/api/api-validation";
import {Order} from "../../model/clearing/order";
import {SignificantOrdersInfo} from "../../model/clearing/significant-orders-info";
import {ExchangeOverride} from "../../model/clearing/allocation/exchange-override";
import {SimpleRequest} from "../../model/api/simple-request";

@Injectable({
  providedIn: 'root'
})
export class ClearingAllocationService extends ParentAPIService implements OnInit, OnDestroy {

  constructor(http: HttpClient,
              private toastService: ToastService) {

    super(http);
  }

  ngOnInit(): void {
  }


  ngOnDestroy() {
  }

  public getFullTrades(tradeIds: string[]): Observable<ApiValidation<Trade[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_FULL_TRADES, tradeIds);
  }

  public getSignificantTrades(tradeIds: string[]): Observable<ApiValidation<Trade[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_SIGNIFICANT_TRADES, tradeIds);
  }

  public getSignificantTradesFromClearings(clearingIds: string[]): Observable<ApiValidation<Trade[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_SIGNIFICANT_TRADES_FROM_CLEARINGS, clearingIds);
  }

  public getSignificantClearingOrders(clearingIds: string[]): Observable<ApiValidation<Order[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_SIGNIFICANT_CLEARING_ORDERS, clearingIds);
  }

  public getSignificantOrders(orderIds: string[]): Observable<ApiValidation<SignificantOrdersInfo>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_SIGNIFICANT_ORDERS, orderIds);
  }

  public getOrderDetail(orderId: string): Observable<ApiValidation<Order>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_ORDER_DETAIL, new SimpleRequest(orderId));
  }

  public getWholeOrder(orderId: string, keepSide: boolean = false): Observable<ApiValidation<Order[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_WHOLE_ORDER, new SimpleRequest(orderId)
      , {'keepSide': keepSide});
  }

  public getOrderLeg(orderId: string): Observable<ApiValidation<Order[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_GET_ORDER_LEG, new SimpleRequest(orderId));
  }

  public singleAllocationRequest(request: AllocationRequest): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_SINGLE_ALLOCATION, request);
  }

  public updateSingleAllocationRequest(request: AllocationRequest): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_UPDATE_SINGLE_ALLOCATION, request);
  }

  public splitAllocationRequest(request: AllocationRequest): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_SPLIT_ALLOCATION, request);
  }

  public updateSplitAllocationRequest(request: AllocationRequest): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_UPDATE_SPLIT_ALLOCATION, request);
  }

  public approveAPSLeg(orderId: string): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_APPROVE_APSLEG, new SimpleRequest(orderId));
  }

  public approveAPSWholeOrder(orderId: string): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_APPROVE_APSWHOLEORDER, new SimpleRequest(orderId));
  }

  public exchangeApsOverride(exchangeOverrides: ExchangeOverride[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_EXCHANGE_OVERRIDE, exchangeOverrides);
  }

  public exchangeApsOverrideOptimizeResidual(orderId): Observable<ApiValidation<Order[]>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_EXCHANGE_OVERRIDE_OPTIMIZE_RESIDUAL, new SimpleRequest(orderId));
  }

  public deleteOrders(orderIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_DELETE, orderIds);
  }

  public requestReversal(orderIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_REQUEST_REVERSAL, orderIds);
  }

  public cancelReversal(orderIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_CANCEL_REVERSAL, orderIds);
  }

  public requestReversalClearing(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_REQUEST_REVERSAL_CLEARING, clearingIds);
  }

  public cancelReversalClearing(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_CANCEL_REVERSAL_CLEARING, clearingIds);
  }

  public acceptReversal(tradeIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_ACCEPT_REVERSAL, tradeIds);
  }

  public rejectReversal(tradeIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.GET_CLEARING_ALLOCATION_REJECT_REVERSAL, tradeIds);
  }

  public disableOrder(orderIds: string[], localIsDisabled: boolean): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_DISABLE_ORDER, orderIds, {localIsDisabled: localIsDisabled});
  }

  public moveOrders(clearingIds: string[], apsGroupId: string, orderReference: string): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_MOVE_ORDERS, clearingIds,
      {apsGroupId: apsGroupId, orderReference: orderReference});
  }

  public cancelClearingError(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_CANCEL_CLEARING_ERROR, clearingIds);
  }

  public cancelPendingClearing(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_CANCEL_PENDING_CLEARING, clearingIds);
  }

  public resendClearing(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_RESEND_CLEARING, clearingIds);
  }

  public forceClear(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_FORCE_CLEAR, clearingIds);
  }

  public deleteClearingError(clearingIds: string[]): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_DELETE_CLEARING_ERROR, clearingIds);
  }

  public overrideCustomReference(tradeIds: string[], customReference: string): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_OVERRIDE_CUSTOM_REFERENCE, tradeIds,
      {customReference: customReference});
  }

  public updateTradePrice(tradeIds: string[], price: number): Observable<ApiValidation<any>> {
    return this.post(CrystalclearClearingBackUrl.POST_CLEARING_ALLOCATION_UPDATE_TRADE_PRICE, tradeIds,
      {price: price});
  }

}
